/**
 * Component for uploading files with drag and drop functionality.
 *
 * @component
 * @example
 * ```tsx
 * <UploadRAC
 *   fileTypes={["image/png", "image/jpeg"]}
 *   onFileSelected={(files) => console.log(files)}
 *   multiple={true}
 *   icon="cloud_upload"
 *   maxSizeBytes={10000000}
 *   isLoading={false}
 *   isDisabled={false}
 *   triggerButton={<button>Upload</button>}
 *   testID="upload-component"
 * />
 * ```
 */
import { rem } from "@certn/cascadia";
import { CascadiaStandardIcon, shapes } from "@certn/cascadia-tokens";
import { DEFAULT_FILE_TYPES } from "@certn/utils";
import { css } from "@emotion/css";
import React, { useEffect, useRef } from "react";
import type { FileDropItem } from "react-aria";
import { FileTrigger, DropZone, DropZoneProps } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { Icon, LinkButton } from "@certn/cascadia-react";

import { FileInputInfoRAC } from "./assets/UploadInfoRAC";
import { Text } from "../../foundations/typography/Text";
import { mq } from "../utils";

export interface UploadRACProps extends DropZoneProps {
  fileTypes?: string[];
  onFileSelected(files: (File | FileDropItem)[]): void;
  multiple?: boolean;
  icon?: CascadiaStandardIcon;
  maxSizeBytes?: number;
  isLoading?: boolean;
  isDisabled?: boolean;
  triggerButton?: React.ReactNode;
  testID?: string;
  onBlur?: () => void;
}

export function UploadRAC(props: UploadRACProps) {
  const { t } = useTranslation();
  const {
    multiple = false,
    icon = "cloud_upload",
    fileTypes = DEFAULT_FILE_TYPES,
    maxSizeBytes = 10000000,
    isLoading,
    isDisabled = isLoading,
    testID = "upload",
    onFileSelected,
    onBlur,
  } = props;

  const uploaderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const uploader = uploaderRef.current;
    const listener = () => {
      onBlur && onBlur();
    };

    uploader?.addEventListener("cancel", listener);
    uploader?.addEventListener("change", listener);

    return () => {
      uploader?.removeEventListener("cancel", listener);
      uploader?.removeEventListener("change", listener);
    };
  }, [uploaderRef, onBlur]);

  if (props.triggerButton) {
    return (
      <FileTrigger
        allowsMultiple={multiple}
        acceptedFileTypes={fileTypes}
        ref={uploaderRef}
        onSelect={(e) => {
          const files = Array.from(e as FileList);
          onFileSelected(files);
        }}
      >
        {props.triggerButton}
      </FileTrigger>
    );
  }

  return (
    <DropZone
      getDropOperation={(e) => {
        if (
          e.has("image/png" || "image/jpeg" || "image/jpg" || "application/pdf")
        ) {
          return "copy";
        }
        return "cancel";
      }}
      onDrop={(e) => {
        const files = e.items.filter(
          (file) => file.kind === "file"
        ) as FileDropItem[];
        onFileSelected(files);
      }}
      className={css(
        styles.borders,
        styles.size,
        styles.colors,
        isDisabled && styles.disabled,
        styles.dragDrop
      )}
      data-testid={`${testID}-drop-zone`}
    >
      <FileTrigger
        allowsMultiple
        acceptedFileTypes={fileTypes}
        ref={uploaderRef}
        onSelect={(e) => {
          const files = Array.from(e as FileList);
          onFileSelected(files);
        }}
      >
        <div>
          <div className={styles.ghostButton}>
            <div className={css(styles.align, styles.spacing)}>
              <Icon
                type="standard"
                name={icon}
                color="icon-weak"
                size="space-6"
              />
            </div>
            <div className={css(styles.textAreaSpacing, styles.align)}>
              <label data-testid={`${testID}-label`}>
                <LinkButton
                  linkStyle="monochrome"
                  textStyle="form/body-primary-link"
                  textColor="text-base"
                  testID="upload-button"
                  isDisabled={isDisabled}
                >
                  Browse files
                </LinkButton>
                <Text
                  style="form/body-primary"
                  color="text-base"
                  // keeping media query styles inline for Text as it doesn't work in styles object
                  className={css(mq({ display: ["none", "inline"] }))}
                >
                  {t(" or drag and drop to upload")}
                </Text>
                <Text
                  style="form/body-primary"
                  color="text-base"
                  className={css(mq({ display: ["inline", "none"] }))}
                >
                  {" "}
                  {t("to upload")}
                </Text>
              </label>
            </div>
          </div>
          <FileInputInfoRAC fileTypes={fileTypes} maxSizeBytes={maxSizeBytes} />
        </div>
      </FileTrigger>
    </DropZone>
  );
}

const styles = {
  wrapper: {
    display: "flex",
  },
  colors: css({
    backgroundColor: "transparent",
  }),
  size: {
    minWidth: "fit-content",
    flexGrow: 1,
    gap: rem("space-3"),
    borderStyle: "dashed",
    borderWidth: "2px",
    borderRadius: shapes["border-radius-2"],
    borderColor: "var(--border-strong)",
  },
  ghostButton: css(
    mq({
      display: ["flex", "block"],
      flexDirection: ["row"],
      alignItems: ["baseline"],
      justifyContent: ["center"],
    })
  ),
  borders: {
    borderRadius: "4px",
  },
  spacing: css(
    {
      paddingBottom: rem("space-3"),
    },
    mq({
      paddingTop: [rem("space-4"), rem("space-5")],
      paddingRight: [rem("space-2"), 0],
    })
  ),
  textAreaSpacing: css(
    {
      paddingBottom: rem("space-1-5"),
    },
    mq({
      display: ["flex", "block"],
      gap: [rem("space-1"), rem("space-0")],
    })
  ),
  align: css({
    textAlign: "center",
  }),
  dragDrop: css({
    "&[data-disabled]": { cursor: "not-allowed" },
  }),
  disabled: { opacity: 0.5 },
};
